// src/services/pixPaymentService.js
// Integração de PIX via AbacatePay (serverless) + persistência no Firestore.

import { db } from '../firebase.js';
import {
  doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs,
} from 'firebase/firestore';

const PAYMENT_AMOUNT = 12.00;     // R$ 3,00 (server normaliza para centavos)
const EXPIRES_MIN = 30;          // expiração de 30 minutos
const PENDING_KEY = 'ys_pending_pix';



class PixPaymentService {
  constructor() {}
  rememberPendingPayment(payment) {
    try {
      const payload = {
        localId: payment.id,
        externalId: payment.externalId,
        expiresAt: payment.expiresAt || null,
        createdAt: Date.now()
      };
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
    } catch {}
  }

  getPendingPayment() {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  clearPendingPayment() {
    try { localStorage.removeItem(PENDING_KEY); } catch {}
  }
  async createPixPayment(userId, userEmail, userName, userPhone = null, userTaxId = null) {
    const paymentId = `PAY_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    const base = {
      id: paymentId,
      userId,
      amount: PAYMENT_AMOUNT,
      currency: 'BRL',
      description: 'Análise de Redação ENEM com IA',
      payerEmail: userEmail,
      payerName: userName || 'Usuário',
      method: 'pix_qrcode',
      status: 'PENDING',
      createdAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + EXPIRES_MIN * 60 * 1000).toISOString(),
    };

    try {
      console.log('=== PIX Payment Service Debug ===');
      console.log('Creating payment with data:', {
        paymentId, userId, userEmail, userName, userPhone, userTaxId
      });

      const requestPayload = {
        amount: PAYMENT_AMOUNT,
        description: base.description,
        paymentType: 'qrcode',
        metadata: { 
          userId,
          paymentId,
          email: userEmail, 
          name: userName,
          cellphone: userPhone,
          taxId: userTaxId || '000.000.000-00'
        },
      };

      console.log('Request payload:', JSON.stringify(requestPayload, null, 2));

      const resp = await fetch('/api/abacatepay/create-pix', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
      });

      console.log('API response status:', resp.status);
      console.log('API response ok:', resp.ok);

      const responseText = await resp.text();
      console.log('API raw response:', responseText);

      let json;
      try {
        json = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse API response:', parseError);
        throw new Error(`Invalid API response: ${responseText}`);
      }

      console.log('Parsed API response:', JSON.stringify(json, null, 2));

      if (!resp.ok) {
        console.error('API error:', json);
        throw new Error(json?.error || `Create PIX error (${resp.status})`);
      }

      console.log('Available fields from API:', {
        id: !!json.id,
        status: json.status,
        method: json.method,
        amount: json.amount,
        paymentUrl: !!json.paymentUrl,
        copyAndPaste: !!json.copyAndPaste,
        qrCodeBase64: !!json.qrCodeBase64,
        qrCodeImage: !!json.qrCodeImage,
        allKeys: Object.keys(json)
      });

      const qrCodeData =
        json.qrCodeBase64 ||
        json.qrCodeImage ||
        json.qrcode ||
        json.qrCode ||
        json?.raw?.data?.brCodeBase64;   // fallback

      const copyPasteData =
        json.copyAndPaste ||
        json.emv ||
        json.qrCode ||
        json?.raw?.data?.brCode;         // fallback
      
      console.log('Extracted data:', {
        qrCodeData: !!qrCodeData,
        copyPasteData: !!copyPasteData,
        qrCodeDataLength: qrCodeData ? qrCodeData.length : 0,
        copyPasteDataLength: copyPasteData ? copyPasteData.length : 0
      });

      const abacate = {
        externalId: json.id,
        status: json.status || 'PENDING',
        method: json.method || 'pix_qrcode',
        copyAndPaste: copyPasteData || null,
        qrCodeBase64: qrCodeData || null,
        qrCodeDataUrl: qrCodeData ? this.createQrCodeDataUrl(qrCodeData) : null,
        qrCodeImage: qrCodeData || null,
        paymentUrl: json.paymentUrl || null,
        expiresAt: json.expiresAt || base.expiresAt,
        raw: json
      };

      console.log('Processed abacate data:', JSON.stringify(abacate, null, 2));

      const merged = { ...base, ...abacate };
      console.log('Final merged payment data:', JSON.stringify(merged, null, 2));
      console.log('=== End PIX Payment Service Debug ===');
      this.rememberPendingPayment(merged);

      await this.savePaymentRecord(userId, merged);
      return merged;
    } catch (e) {
      console.error('Error creating PIX payment (AbacatePay):', e);
      throw new Error('Falha ao criar pagamento PIX. Tente novamente.');
    }
  }

  createQrCodeDataUrl(base64Data) {
    if (!base64Data) return null;
    if (base64Data.startsWith('data:')) return base64Data;
    if (base64Data.startsWith('http')) return base64Data;
    if (base64Data.length > 50) return `data:image/png;base64,${base64Data}`;
    return null;
  }

  async savePaymentRecord(userId, paymentData) {
    try {
      const ref = doc(db, 'payments', paymentData.id);
      await setDoc(ref, { ...paymentData, userId, timestamp: new Date() });
      console.log('Payment record saved to Firestore:', paymentData.id);
    } catch (e) {
      console.error('Error saving payment record:', e);
    }
  }

  async checkPaymentStatus(localPaymentId) {
    try {
      console.log('=== Checking Payment Status ===');
      console.log('Local payment ID:', localPaymentId);

      const ref = doc(db, 'payments', localPaymentId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        console.error('Payment not found in Firestore:', localPaymentId);
        throw new Error('Payment not found');
      }

      const data = snap.data();
      console.log('Local payment data:', JSON.stringify(data, null, 2));

      if (data.status === 'PAID') {
        console.log('Payment already marked as PAID locally');
        return { ...data, status: 'PAID' };
      }

      if (data.externalId) {
        console.log('Checking external status for:', data.externalId, 'method:', data.method);
        const externalStatus = await this.checkExternalPaymentStatus(data.externalId, data.method);
        console.log('External status result:', externalStatus);
        
        if (externalStatus === 'PAID') {
          console.log('Payment confirmed as PAID, updating local record');
          await this.markPaymentAsPaid(localPaymentId);
          const updated = await getDoc(ref);
          return { ...updated.data(), status: 'PAID' };
        }

        if (['EXPIRED', 'CANCELLED', 'REFUNDED'].includes(externalStatus)) {
          this.clearPendingPayment();

          console.log('Payment status changed to:', externalStatus);
          await this.updateLocalStatus(localPaymentId, externalStatus);
          const updated = await getDoc(ref);
          return { ...updated.data(), status: externalStatus };
        }
      } else {
        console.warn('No externalId found for payment:', localPaymentId);
      }

      console.log('Payment status remains:', data.status || 'PENDING');
      console.log('=== End Status Check ===');
      return { ...data, status: data.status || 'PENDING' };
    } catch (e) {
      console.error('checkPaymentStatus error:', e);
      throw e;
    }
  }

  // Uses our fixed Next.js route that proxies AbacatePay /pixQrCode/check
  async checkExternalPaymentStatus(externalId, method = 'pix_qrcode') {
    try {
      console.log('Checking external payment status:', { externalId, method });

      const url = `/api/abacatepay/check-pix?id=${encodeURIComponent(externalId)}&method=${encodeURIComponent(method)}`;
      console.log('Status check URL:', url);

      const resp = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      console.log('Status API response:', resp.status, resp.ok);

      const responseText = await resp.text();
      console.log('Status API raw response:', responseText);

      let json;
      try {
        json = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse status response:', parseError);
        throw new Error(`Status-check returned non-JSON: ${responseText.slice(0, 200)}`);
      }

      console.log('Status API parsed response:', JSON.stringify(json, null, 2));

      if (!resp.ok) {
        throw new Error(`Status-check HTTP ${resp.status}: ${JSON.stringify(json).slice(0, 200)}`);
      }

      const raw = (json?.data?.status || json?.status || '').toString().toUpperCase();
      const status =
        raw === 'PAID' || raw === 'COMPLETED' ? 'PAID' :
        raw === 'EXPIRED'                     ? 'EXPIRED' :
        raw === 'CANCELLED' || raw === 'CANCELED' ? 'CANCELLED' :
        'PENDING';

      console.log('Extracted status:', status);
      return status;
    } catch (e) {
      console.error('checkExternalPaymentStatus error:', e);
      throw e;
    }
  }
  
  async updateLocalStatus(paymentId, status) {
    try {
      console.log('Updating local status:', { paymentId, status });
      const ref = doc(db, 'payments', paymentId);
      await updateDoc(ref, { status, updatedAt: new Date() });
    } catch (e) {
      console.error('updateLocalStatus error:', e);
    }
  }

  async markPaymentAsPaid(paymentId) {
    try {
      console.log('Marking payment as paid:', paymentId);
      const ref = doc(db, 'payments', paymentId);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : null;
      const userId = data?.userId;

      await updateDoc(ref, { 
        status: 'PAID', 
        paidAt: new Date(), 
        updatedAt: new Date() 
      });

      if (userId) {
        console.log('Updating user payment status:', userId);
        const up = doc(db, 'userPayments', userId);
        await setDoc(
          up,
          {
            userId,
            lastPaymentId: paymentId,
            hasPaid: true,
            status: 'PAID',
            amount: data?.amount || PAYMENT_AMOUNT,
            paidAt: new Date(),
          },
          { merge: true }
        );
      }
      
      console.log('Payment marked as paid successfully');
      this.clearPendingPayment();
      return true;
    } catch (e) {
      console.error('markPaymentAsPaid error:', e);
      return false;
    }
  }

  async hasUserPaid(userId) {
    try {
      const ref = doc(db, 'userPayments', userId);
      const snap = await getDoc(ref);
      console.log("UserPaymet: ", snap);
      if (!snap.exists()) return false;
      const d = snap.data();
      const paidAt = d.paidAt?.toDate ? d.paidAt.toDate() : (d.paidAt ? new Date(d.paidAt) : null);
      console.log("Pagou em: ", paidAt);
      if (!paidAt) return false;
      const hours = (Date.now() - paidAt.getTime()) / 36e5;
      return d.hasPaid === true && d.status === 'PAID';
    } catch (e) {
      console.error('hasUserPaid error:', e);
      return false;
    }
  }

  async checkUserPaymentStatus(userId) {
    return this.hasUserPaid(userId);
  }

  async getUserPaymentHistory(userId) {
    try {
      const paymentsRef = collection(db, 'payments');
      const qy = query(paymentsRef, where('userId', '==', userId));
      const snap = await getDocs(qy);
      const arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      return arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (e) {
      console.error('getUserPaymentHistory error:', e);
      return [];
    }
  }
}

async function sleep(ms){ return new Promise(r => setTimeout(r, ms)); }

export async function reconcileUserPendingPayments(userId) {
  try {
    const paymentsRef = collection(db, 'payments');
    const qy = query(paymentsRef, where('userId', '==', userId), where('status', '==', 'PENDING'));
    const snap = await getDocs(qy);
    const work = [];
    snap.forEach((d) => {
      const p = d.data();
      if (p?.externalId) {
        work.push((async () => {
          // backoff a smidge to avoid spikes
          await sleep(150);
          try {
            // use your existing function that normalizes AbacatePay status
            const s = await (await import('./pixPaymentService.js')).default
              .checkExternalPaymentStatus(p.externalId, p.method || 'pix_qrcode');
            // If paid/expired/etc, your existing checkPaymentStatus &
            // markPaymentAsPaid/updateLocalStatus already handle Firestore updates.
          } catch (err) {
            console.warn('reconcile check failed for', p.externalId, err);
          }
        })());
      }
    });
    await Promise.allSettled(work);
  } catch (e) {
    console.error('reconcileUserPendingPayments error:', e);
  }
}

export default new PixPaymentService();
